import { useEffect } from 'react'
import { Checkbox, SegmentedControl, Stack, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import {
  buildFindingsSyntheticArgs,
  extractConfirm,
  isDryRun,
  syntheticAction,
  WIPE_ALL_CONFIRMATION,
  type SyntheticAction,
} from './FindingsSyntheticArgs.utils'

const ACTION_TEXT: Record<SyntheticAction, string> = {
  generate: 'Generate a synthetic 3-year findings history for every active patient',
  'wipe-synthetic': 'Wipe the synthetic findings rows (converted ones stay)',
  'wipe-all': 'Wipe every findings row, converted ones included',
}

/**
 * Args editor/viewer for findings.synthetic (dev/staging only): generate a synthetic
 * findings history, or wipe the synthetic rows / every row. Both default to a dry run that
 * only reports what it would write or delete.
 */
export function FindingsSyntheticArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const action = syntheticAction(value)
  const dryRun = isDryRun(value)
  const confirm = extractConfirm(value)

  // Edit mode opened with no args: seed the default (a dry-run generate) so the dialog opens valid.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.(buildFindingsSyntheticArgs('generate', true, ''))
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return (
      <Text size="sm">
        <b>{ACTION_TEXT[action]}</b>
        {dryRun ? ' · dry run, nothing written' : ''}
      </Text>
    )
  }

  const change = (nextAction: SyntheticAction, nextDryRun: boolean, nextConfirm: string) =>
    onChange?.(buildFindingsSyntheticArgs(nextAction, nextDryRun, nextConfirm))

  return (
    <Stack gap="sm">
      <div>
        <Text size="sm" fw={500} mb={4}>
          Action
        </Text>
        <SegmentedControl
          aria-label="Action"
          value={action}
          data={[
            { label: 'Generate', value: 'generate' },
            { label: 'Wipe synthetic', value: 'wipe-synthetic' },
            { label: 'Wipe all', value: 'wipe-all' },
          ]}
          onChange={(next) => change(next as SyntheticAction, dryRun, '')}
        />
        <Text size="xs" c="dimmed" mt={4}>
          {ACTION_TEXT[action]}.
          {action === 'generate' && ' Refused while synthetic rows exist — wipe them first.'}
          {action === 'wipe-all' &&
            " Restore the converted findings afterwards with findings.sync { since: 'all' }."}
        </Text>
      </div>
      {action === 'wipe-all' && (
        <TextInput
          label="Confirmation"
          description={`Type ${WIPE_ALL_CONFIRMATION} to wipe every findings row.`}
          placeholder={WIPE_ALL_CONFIRMATION}
          value={confirm}
          onChange={(event) => change(action, dryRun, event.currentTarget.value)}
        />
      )}
      <Checkbox
        label="Dry run — report the counts, write nothing"
        checked={dryRun}
        onChange={(event) => change(action, event.currentTarget.checked, confirm)}
      />
    </Stack>
  )
}
